import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { dispatch } from '../../utils/event-helpers';

type BannerTheme = 'default' | 'brand' | string;
type BannerVariant = 'floating' | 'full-width';

/**
 * Page-level announcement bar for promotions, system notices, and important messages.
 *
 * Renders an inline/horizontal layout on desktop (icon → text content → actions → dismiss)
 * and a stacked/card layout on mobile (icon + dismiss on top row, text and full-width
 * actions below). Supports dismissible and persistent behavior, two visual themes
 * (`default` and `brand`), and two layout variants (`floating` and `full-width`).
 *
 * The component is presentational — it emits an event on dismiss but never removes
 * itself from the DOM. The consumer controls visibility.
 *
 * @element asc-banner
 *
 * @fires asc-dismiss - Fired when the user activates the dismiss button. Detail: `{}`
 *
 * @slot title - **Required.** Primary bold text message.
 * @slot description - Optional secondary supporting text.
 * @slot icon - Optional leading icon. Accepts `<svg aria-hidden="true">` or `<asc-icon>`.
 * @slot actions - Optional action controls (0–2 button components).
 *
 * @csspart base        - Root `<section>` element; layout, background, border, shadow.
 * @csspart icon-wrapper - Wraps the icon slot; hidden when no icon is slotted.
 * @csspart content     - Wraps the title and description slots; grows to fill available width.
 * @csspart actions     - Wraps the `actions` slot; `<footer>`.
 * @csspart dismiss     - The dismiss `<button>`; only rendered when `dismissible` is `true`.
 *
 * @cssprop --asc-banner-bg                      - Background color. Default: `var(--asc-bg-secondary_subtle)`
 * @cssprop --asc-banner-border-color             - Border color. Default: `var(--asc-border-secondary)`
 * @cssprop --asc-banner-border-width             - Border width. Default: `var(--asc-border-width)`
 * @cssprop --asc-banner-border-radius            - Border radius (floating mode). Default: `var(--asc-radius-md)`
 * @cssprop --asc-banner-shadow                   - Box shadow (floating mode). Default: lg shadow tokens
 * @cssprop --asc-banner-padding                  - Padding. Default: `var(--asc-spacing-lg)`
 * @cssprop --asc-banner-gap                     - Gap between layout sections. Default: `var(--asc-spacing-lg)`
 * @cssprop --asc-banner-content-gap             - Gap between icon and text content. Default: `var(--asc-spacing-xl)`
 * @cssprop --asc-banner-title-color             - Title text color. Default: `var(--asc-text-secondary)`
 * @cssprop --asc-banner-title-font-weight       - Title font weight. Default: `var(--asc-font-weight-semibold)`
 * @cssprop --asc-banner-title-font-size         - Title font size. Default: `var(--asc-font-size-text-md)`
 * @cssprop --asc-banner-description-color       - Description text color. Default: `var(--asc-text-tertiary)`
 * @cssprop --asc-banner-description-font-size   - Description font size. Default: `var(--asc-font-size-text-md)`
 * @cssprop --asc-banner-icon-color              - Icon foreground color. Default: `var(--asc-fg-brand-primary)`
 * @cssprop --asc-banner-dismiss-color           - Dismiss button icon color. Default: `var(--asc-fg-tertiary)`
 * @cssprop --asc-banner-dismiss-color-hover     - Dismiss button icon hover color. Default: `var(--asc-fg-primary)`
 * @cssprop --asc-banner-focus-ring-color        - Focus ring color. Default: `var(--asc-focus-ring)`
 */
@customElement('asc-banner')
export class AscBanner extends LitElement {
  static override styles = css`
    :host {
      display: block;
      container-type: inline-size;
    }

    [part='base'] {
      display: grid;
      grid-template-columns: auto 1fr auto auto;
      grid-template-areas: 'icon content actions dismiss';
      align-items: center;
      row-gap: var(--asc-banner-gap, var(--asc-spacing-lg));
      column-gap: 0;
      padding: var(--asc-banner-padding, var(--asc-spacing-lg));
      background: var(--asc-banner-bg, var(--asc-bg-secondary_subtle));
      border: var(--asc-banner-border-width, var(--asc-border-width)) solid
        var(--asc-banner-border-color, var(--asc-border-secondary));
      border-radius: var(--asc-banner-border-radius, var(--asc-radius-md));
      box-shadow: var(
        --asc-banner-shadow,
        0px 12px 16px -4px var(--asc-shadow-lg_01),
        0px 4px 6px -2px var(--asc-shadow-lg_02),
        0px 2px 2px -1px var(--asc-shadow-lg_03)
      );
      font-family: var(--asc-font-family-body);
      box-sizing: border-box;
    }

    /* ─── Grid areas ───────────────────────────────── */
    [part='icon-wrapper'] {
      grid-area: icon;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      margin-inline-end: var(--asc-banner-gap, var(--asc-spacing-lg));
      color: var(--asc-banner-icon-color, var(--asc-fg-brand-primary));
    }

    [part='icon-wrapper'][hidden] {
      display: none;
    }

    [part='content'] {
      grid-area: content;
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: var(--asc-banner-content-gap, var(--asc-spacing-xl));
      min-width: 0;
    }

    ::slotted([slot='title']) {
      color: var(--asc-banner-title-color, var(--asc-text-secondary));
      font-weight: var(--asc-banner-title-font-weight, var(--asc-font-weight-semibold));
      font-size: var(--asc-banner-title-font-size, var(--asc-font-size-text-md));
      line-height: var(--asc-line-height-text-md);
    }

    ::slotted([slot='description']) {
      color: var(--asc-banner-description-color, var(--asc-text-tertiary));
      font-size: var(--asc-banner-description-font-size, var(--asc-font-size-text-md));
      line-height: var(--asc-line-height-text-md);
    }

    [part='actions'] {
      grid-area: actions;
      display: flex;
      align-items: center;
      gap: var(--asc-spacing-md);
    }

    /* ─── Dismiss button ───────────────────────────── */
    [part='dismiss'] {
      grid-area: dismiss;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: var(--asc-spacing-md);
      border: none;
      border-radius: var(--asc-radius-md);
      background: transparent;
      color: var(--asc-banner-dismiss-color, var(--asc-fg-tertiary));
      cursor: pointer;
      line-height: 0;
    }

    [part='dismiss']:hover {
      color: var(--asc-banner-dismiss-color-hover, var(--asc-fg-primary));
    }

    [part='dismiss']:focus-visible {
      outline: 2px solid var(--asc-banner-focus-ring-color, var(--asc-focus-ring));
      outline-offset: 2px;
    }

    [part='dismiss'] svg {
      width: 20px;
      height: 20px;
    }

    /* ─── Brand theme ──────────────────────────────── */
    :host([theme='brand']) [part='base'] {
      --asc-banner-bg: var(--asc-bg-brand-primary);
      --asc-banner-border-color: var(--asc-border-brand);
      --asc-banner-title-color: var(--asc-text-primary);
      --asc-banner-description-color: var(--asc-text-secondary);
      --asc-banner-icon-color: var(--asc-fg-brand-primary);
      --asc-banner-dismiss-color: var(--asc-fg-tertiary);
      --asc-banner-dismiss-color-hover: var(--asc-fg-primary);
    }

    /* ─── Full-width mode ──────────────────────────── */
    :host([variant='full-width']) [part='base'] {
      border-radius: var(--asc-banner-border-radius, var(--asc-radius-md))
        var(--asc-banner-border-radius, var(--asc-radius-md)) 0 0;
      box-shadow: none;
      width: 100%;
      border-top: none;
      border-left: none;
      border-right: none;
    }

    /* ─── Stacked layout (mobile / narrow) ─────────── */
    @container (max-width: 375px) {
      [part='base'] {
        grid-template-columns: auto 1fr;
        grid-template-areas:
          'icon dismiss'
          'content content'
          'actions actions';
        align-items: start;
      }

      [part='dismiss'] {
        justify-self: end;
      }

      [part='content'] {
        flex-direction: column;
        gap: var(--asc-spacing-xxs);
      }

      [part='actions'] {
        flex-direction: column;
        gap: var(--asc-spacing-lg);
        width: 100%;
      }

      ::slotted([slot='actions']) {
        width: 100%;
      }
    }

    /* ─── Full-width + mobile: top border only ─────── */
    @container (max-width: 375px) {
      :host([variant='full-width']) [part='base'] {
        border-top: var(--asc-banner-border-width, var(--asc-border-width)) solid
          var(--asc-banner-border-color, var(--asc-border-secondary));
        border-bottom: none;
        border-radius: var(--asc-radius-none);
      }
    }
  `;

  /** Defines the visual color scheme. 'default' = subtle neutral; 'brand' = light brand-tinted with brand border. */
  @property({ type: String, reflect: true }) theme: BannerTheme = 'default';

  /** Defines the layout variant. 'floating' = contained card; 'full-width' = edge-to-edge bar. */
  @property({ type: String, reflect: true }) variant: BannerVariant = 'floating';

  /** When true, renders a dismiss (×) button in the top-right corner. */
  @property({ type: Boolean, reflect: true }) dismissible = false;

  /** Accessible label for the inner `<section>` element. Used as `aria-label` on the landmark region. */
  @property({ type: String }) label = 'Banner';

  /** Accessible label for the dismiss button. Override to provide banner-specific context. */
  @property({ type: String, attribute: 'dismiss-label' }) dismissLabel = 'Dismiss banner';

  @state() private _hasIcon = false;

  private _handleIconSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._hasIcon = slot.assignedElements().length > 0;
  }

  private _handleDismiss(): void {
    dispatch<Record<string, never>>(this, 'asc-dismiss', { detail: {} });
  }

  override render() {
    return html`
      <section part="base" aria-label="${this.label}">
        <div part="icon-wrapper" ?hidden="${!this._hasIcon}">
          <slot name="icon" @slotchange="${this._handleIconSlotChange}"></slot>
        </div>
        <div part="content">
          <slot name="title"></slot>
          <slot name="description"></slot>
        </div>
        <footer part="actions">
          <slot name="actions"></slot>
        </footer>
        ${this.dismissible
          ? html`
              <button
                part="dismiss"
                type="button"
                aria-label="${this.dismissLabel}"
                @click="${this._handleDismiss}"
              >
                <svg
                  aria-hidden="true"
                  focusable="false"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            `
          : nothing}
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-banner': AscBanner;
  }
}
